import { useState } from "react";
import { Sparkles } from "lucide-react";
import PreviewField from "@/components/automation/PreviewField";

// "Preview as Sales User" pane — renders the flow exactly as a rep would
// experience it (step progress + Back/Next + conditional visibility).
// Receives the draft flow + step order + options via props; owns only local
// preview answer/step state (no global Flow Builder state, no fetch/save).
const stepOf = (q) => q.step || "General";

export default function SalesPreview({ draft, stepOrder, options }) {
  const [answers, setAnswers] = useState({});
  const [stepIdx, setStepIdx] = useState(0);
  const qVisible = (q) => {
    const c = q.conditional;
    if (!c || !c.question_id) return true;
    const v = answers[c.question_id];
    const op = c.op || "equals";
    if (op === "equals") return String(v) === String(c.value);
    if (op === "not_equals") return String(v) !== String(c.value);
    if (op === "contains") return Array.isArray(v) ? v.includes(c.value) : String(v || "").includes(String(c.value));
    return true;
  };
  const segQ = (draft.questions || []).find((q) => q.field_mapping === "business_segment");
  const segAnswer = segQ ? answers[segQ.question_id] : null;
  const catOptions = options?.business_categories?.[segAnswer] || [];
  const curStep = stepOrder[Math.min(stepIdx, stepOrder.length - 1)];
  const stepQs = draft.questions.filter((q) => stepOf(q) === curStep && qVisible(q));
  const isLast = stepIdx >= stepOrder.length - 1;
  return (
    <div className="mx-auto max-w-md" data-testid="flow-preview-pane">
      <div className="rounded-2xl border border-sky/70 bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-loop"><Sparkles className="h-3.5 w-3.5" />Sales User Preview</div>
        <h3 className="font-sora text-base font-bold text-navy">{draft.name || "Untitled flow"}</h3>
        {stepOrder.length > 1 && (
          <div className="mt-3" data-testid="preview-step-progress">
            <div className="mb-1.5 text-[11px] font-semibold text-loop">Step {stepIdx + 1} of {stepOrder.length} · {curStep}</div>
            <div className="flex gap-1">{stepOrder.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full ${i <= stepIdx ? "bg-loop" : "bg-sky"}`} />)}</div>
          </div>
        )}
        <div className="mt-4 space-y-4">
          {stepQs.map((q) => (
            <div key={q.question_id}>
              <label className="mb-1 block text-xs font-semibold text-ink/70">{q.question_label || "(untitled question)"}{q.required && <span className="text-red-400"> *</span>}</label>
              <PreviewField q={q} value={answers[q.question_id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.question_id]: v }))} catOptions={catOptions} />
            </div>
          ))}
          {stepQs.length === 0 && <p className="py-6 text-center text-sm text-ink/40">No visible questions in this step.</p>}
        </div>
        {stepOrder.length > 1 && (
          <div className="mt-5 flex gap-2">
            <button type="button" disabled={stepIdx === 0} onClick={() => setStepIdx((s) => Math.max(0, s - 1))} data-testid="preview-back" className="h-10 flex-1 rounded-xl border border-sky text-sm font-semibold text-ink/70 hover:bg-mist disabled:opacity-40">Back</button>
            <button type="button" disabled={isLast} onClick={() => setStepIdx((s) => s + 1)} data-testid="preview-next" className="h-10 flex-[2] rounded-xl bg-loop text-sm font-semibold text-white hover:bg-loophover disabled:opacity-40">{isLast ? "End of form" : "Next"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
